import { useTranslations } from "next-intl";
import { useState } from "react";

import { Icon } from "@zapier/zinnia-icons";
import { Typeahead, TypeaheadItem } from "@zapier/design-system";

import useFolders, { FolderType } from "./useFolders";

const LOAD_MORE_KEY = "__load_more__";
const LOADING_KEY = "__loading__";

type FolderFilterProps = {
  accountId?: string;
  /** Pass to render selected items outside of dropdown */
  children?: (props: {
    folderIds: string[];
    selectedFolders: FolderType[];
  }) => React.ReactElement[];
  menuWidth?: string;
  id?: string;
  onChange: (value: string | undefined) => void;
  value?: string[] | undefined;
};

type FolderItem = FolderType & { name: string; __isLoadMore?: boolean };

function toItem(folder: FolderType): FolderItem {
  return { ...folder, name: folder.title };
}

function FolderFilter(props: FolderFilterProps) {
  const t = useTranslations("history-page.filters.folder-filter");
  const tCommon = useTranslations("common");
  const folderIds = props.value || [];
  const [inputValue, setInputValue] = useState("");
  const { accountId, onChange } = props;
  const {
    folders,
    isLoading,
    isLoadingMore,
    onLoadMore,
    onSearch,
    selectedFolders,
  } = useFolders({ accountId, folderIds });

  const showLoadMore = Boolean(onLoadMore) && !isLoadingMore;

  const allItems: FolderItem[] = [
    ...folders.map(toItem),
    ...(isLoadingMore
      ? [
          {
            id: LOADING_KEY,
            title: tCommon("loading"),
            name: tCommon("loading"),
            __isLoadMore: true,
          } as FolderItem,
        ]
      : showLoadMore
        ? [
            {
              id: LOAD_MORE_KEY,
              title: tCommon("load-more"),
              name: tCommon("load-more"),
              __isLoadMore: true,
            } as FolderItem,
          ]
        : []),
  ];

  return (
    <>
      <Typeahead
        aria-label={t("search-aria-label")}
        iconLeft="navFolder"
        isBusy={isLoading}
        id={props.id}
        inputValue={inputValue}
        items={allItems}
        menuTrigger="focus"
        menuWidth={props.menuWidth}
        onInputChange={(v) => {
          setInputValue(v);
          onSearch(v);
        }}
        onSelectionChange={(key) => {
          if (key === LOAD_MORE_KEY) {
            onLoadMore?.();
            return;
          }
          if (key === LOADING_KEY) return;
          onChange(key ? String(key) : undefined);
          setInputValue("");
        }}
        placeholder={t("placeholder")}
        selectedKey={null}
        size="medium"
      >
        {(dsItem) => {
          const item = dsItem as FolderItem;
          return (
            <TypeaheadItem
              id={String(item.id)}
              isDisabled={item.id === LOADING_KEY}
              textValue={item.name}
            >
              {item.__isLoadMore ? (
                item.name
              ) : (
                <>
                  <Icon name="navFolder" size={24} />
                  {item.name}
                </>
              )}
            </TypeaheadItem>
          );
        }}
      </Typeahead>
      {props.children && props.children({ folderIds, selectedFolders })}
    </>
  );
}

export default FolderFilter;
